/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Buckingham parameter packs for ionic crystals — the data behind the
 * 'buckingham' force field in crystalForceFields.ts.
 *
 * Each pack carries the Buckingham short-range parameters (per element
 * pair) AND the formal point charges they were fitted with: the two are
 * inseparable — a parameter set fitted to formal ±2 charges is
 * meaningless run with any other charge model.
 *
 * Parameters are pair-tabulated, NOT guessed from a combining rule, so
 * each ionic composition needs its own literature-sourced entry. The
 * 'buckingham' force field `appliesTo` only the compositions registered
 * here; every other crystal falls back to the generic LJ force field.
 */
import { CrystalState } from '../../types.ts';
import { BuckinghamPair } from './PairPotential.ts';

export interface IonicPack {
  /** Literature source for the parameter set + charge model. */
  source: string;
  /** Formal point charges (e) the Buckingham parameters were fitted with. */
  charges: Record<string, number>;
  /** Buckingham parameters keyed by element pair, e.g. 'Mg-O'. */
  pairs: Record<string, BuckinghamPair>;
  /**
   * When true, the ionic FF will NOT call minimize() during setup.
   *
   * Buckingham A·exp(−r/ρ) − C/r⁶ has no inner r⁻¹² wall — at r → 0
   * the −C/r⁶ term diverges to −∞ and the exponential plateaus, so
   * the potential has a spurious deep "inner minimum" at sub-Å r.
   * Most ionic packs with strong cation-anion Coulomb (formal ±2
   * oxides, ±1 halides) are nonetheless stable under minimize()
   * because the physical equilibrium has a large barrier separating
   * it from the inner pathology. BKS silica is the exception — its
   * milder partial charges (Si = +2.4, O = -1.2) put the physical
   * Si-O minimum (~1.6 Å) close enough to the unphysical inner well
   * that the relaxer can tunnel through.
   *
   * For packs where this matters, set skipMinimize and the FF will
   * leave the loaded preset geometry untouched. The preset already
   * sits at (or near) the experimental equilibrium for these models.
   */
  skipMinimize?: boolean;
  /**
   * Lennard-Jones parameters the HOST atoms present to guest molecules
   * (crystalGuests.ts), per element — σ in Å, ε/k_B in K. Without it the
   * guest overlay falls back to UFF, which is wrong for a framework whose
   * cations are buried: UFF Si (σ 3.83 Å, ε 202 K) bound Kr in chabazite at
   * −25 kJ/mol and put a 10 kJ/mol barrier inside silicalite's open
   * channels. Adsorption models treat framework Si as shielded.
   */
  // charge: the partial charges the LJ was fitted with — used by the sieve
  // free-energy tool's electrostatics, NOT by the lab (whose framework keeps
  // its own BKS charges).
  guestHostLJ?: Record<string, { sigma: number; epsilonK: number; charge?: number }>;
}

/**
 * Registry keyed by composition — the sorted, comma-joined set of
 * element symbols in the crystal basis ('Mg,O', 'Ca,O', …). Polymorphs
 * of one composition (rutile vs anatase TiO₂) share an entry.
 *
 * Seeded with MgO (Lewis & Catlow). Adding an ionic crystal is one
 * literature-sourced entry here — keep the charges and the parameters
 * from the SAME source; do not mix sets. Candidate next entries and
 * their standard sources:
 *   - alkali halides (NaCl, KCl, LiF, …) → Tosi & Fumi, J. Phys. Chem.
 *     Solids 25 (1964) 31, converted from their Born-Mayer-Huggins
 *     A·e^[(σ−r)/ρ] − C/r⁶ − D/r⁸ form to this exp-6 form;
 *   - other rocksalt oxides (CaO, SrO, NiO, MnO) → Lewis & Catlow, which
 *     reuse the canonical O-O term below;
 *   - fluorites (CaF₂, SrF₂, BaF₂) and perovskites (SrTiO₃, BaTiO₃) →
 *     the Catlow / Bush parameter families.
 */
export const IONIC_PACKS: Record<string, IonicPack> = {
  // MgO — Lewis & Catlow, J. Phys. C: Solid State Phys. 18 (1985) 1149.
  // Rigid-ion model, formal charges. The O-O term is the canonical
  // Catlow oxygen Buckingham used across the oxide literature; Mg-Mg is
  // omitted by design — cation-cation pairs have no short-range term.
  'Mg,O': {
    source: 'Lewis & Catlow (1985), rigid-ion, formal charges',
    charges: { Mg: 2, O: -2 },
    pairs: {
      'Mg-O': { A: 1428.5, rho: 0.2945, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // Alkali halides — Tosi & Fumi, J. Phys. Chem. Solids 25 (1964) 31.
  // The original BMH form is V = b·B_ij·exp[(σ_i+σ_j−r)/ρ] − C/r⁶ − D/r⁸.
  // We convert to pure exp-6 V = A·exp(−r/ρ) − C/r⁶ by folding the σ-sum
  // offset into the prefactor (A = b·B_ij·exp[(σ_i+σ_j)/ρ]) and dropping
  // the D/r⁸ tail (a ~5 % dispersion correction; acceptable for the
  // qualitative simulations this app runs).
  //
  // Universal inputs across the alkali halide family:
  //   b = 0.21088 eV                  (Pauling prefactor, = 0.338×10⁻¹⁹ J)
  //   B_ij = 1 + z_i/n_i + z_j/n_j    (Pauling factor)
  //     n = 2 for Li⁺ (He-like K shell), 8 for everything else
  //     ⇒ B_LiLi = 2.000   B_LiX = 1.375   (X = F⁻, Cl⁻, …)
  //       B_MM   = 1.250   B_MX  = 1.000   B_XX = 0.750  (M = Na⁺, K⁺)
  //   Pauling basic radii σ (Å):
  //     Li⁺ 0.882   Na⁺ 1.170   K⁺ 1.463   Cs⁺ 1.720
  //     F⁻  1.179   Cl⁻ 1.585   Br⁻ 1.716   I⁻  1.907
  // Per-salt ρ and the resulting A values are documented in each entry.
  // C values are from the Tosi-Fumi tables.

  // NaCl  — ρ = 0.3170 Å.
  //   exp((σ_i+σ_j)/ρ): NaNa  1607      NaCl  5953      ClCl 22026
  //   A = b·B·exp(...): 423.6           1255.4          3483.2   (eV)
  //   C from Tosi-Fumi:   1.68            11.2           116.0   (eV·Å⁶)
  'Cl,Na': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Na: 1, Cl: -1 },
    pairs: {
      'Na-Na': { A: 423.6, rho: 0.317, C: 1.68 },
      'Na-Cl': { A: 1255.4, rho: 0.317, C: 11.2 },
      'Cl-Cl': { A: 3483.2, rho: 0.317, C: 116.0 },
    },
  },

  // LiF  — ρ = 0.2990 Å.   Li⁺ uses B_LiLi=2.000 (n=2), B_LiF=1.375.
  //   exp((σ_i+σ_j)/ρ): LiLi 365.0     LiF   985.4      FF   2666
  //   A = b·B·exp(...): 153.9           285.8           421.6    (eV)
  //   C from Tosi-Fumi:   0.073           0.83           17.0    (eV·Å⁶)
  'F,Li': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Li: 1, F: -1 },
    pairs: {
      'Li-Li': { A: 153.9, rho: 0.299, C: 0.073 },
      'Li-F': { A: 285.8, rho: 0.299, C: 0.83 },
      'F-F': { A: 421.6, rho: 0.299, C: 17.0 },
    },
  },

  // NaF  — ρ = 0.2900 Å.
  //   exp((σ_i+σ_j)/ρ): NaNa 3185      NaF  3288       FF   3391
  //   A = b·B·exp(...): 839.5           693.5          536.4   (eV)
  //   C from Tosi-Fumi:   1.68            4.5           17.0   (eV·Å⁶)
  'F,Na': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Na: 1, F: -1 },
    pairs: {
      'Na-Na': { A: 839.5, rho: 0.29, C: 1.68 },
      'Na-F': { A: 693.5, rho: 0.29, C: 4.5 },
      'F-F': { A: 536.4, rho: 0.29, C: 17.0 },
    },
  },

  // KCl  — ρ = 0.3370 Å.
  //   exp((σ_i+σ_j)/ρ): KK   5910      KCl  8487       ClCl 12180
  //   A = b·B·exp(...): 1558            1789           1926    (eV)
  //   C from Tosi-Fumi:   24.3            48.0          124.5  (eV·Å⁶)
  'Cl,K': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { K: 1, Cl: -1 },
    pairs: {
      'K-K': { A: 1558, rho: 0.337, C: 24.3 },
      'K-Cl': { A: 1789, rho: 0.337, C: 48.0 },
      'Cl-Cl': { A: 1926, rho: 0.337, C: 124.5 },
    },
  },

  // KBr  — ρ = 0.3350 Å.
  //   exp((σ_i+σ_j)/ρ): KK   6225      KBr  13267      BrBr 28198
  //   A = b·B·exp(...): 1641            2798           4461    (eV)
  //   C from Tosi-Fumi:   24.3            73.0          196.0  (eV·Å⁶)
  'Br,K': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { K: 1, Br: -1 },
    pairs: {
      'K-K': { A: 1641, rho: 0.335, C: 24.3 },
      'K-Br': { A: 2798, rho: 0.335, C: 73.0 },
      'Br-Br': { A: 4461, rho: 0.335, C: 196.0 },
    },
  },

  // NaI  — ρ = 0.3510 Å.   Rocksalt structure (Fm-3m), like NaCl.
  //   exp((σ_i+σ_j)/ρ): NaNa  786      NaI   6403      II   52400
  //   A = b·B·exp(...): 207.2           1350.6          8285    (eV)
  //   C from Tosi-Fumi:   1.68            19.1           392.0  (eV·Å⁶)
  'I,Na': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Na: 1, I: -1 },
    pairs: {
      'Na-Na': { A: 207.2, rho: 0.351, C: 1.68 },
      'Na-I': { A: 1350.6, rho: 0.351, C: 19.1 },
      'I-I': { A: 8285, rho: 0.351, C: 392.0 },
    },
  },

  // CsCl — ρ = 0.3350 Å.   CsCl (B2) structure (Pm-3m), not rocksalt.
  // The Buckingham is structure-agnostic: pair sums work over the
  // 8-coordinate B2 lattice the same way they do over rocksalt's 6.
  //   exp((σ_i+σ_j)/ρ): CsCs 28828     CsCl 19258      ClCl 12869
  //   A = b·B·exp(...): 7598            4061           2036    (eV)
  //   C from Tosi-Fumi:   152.0           129.0          105.0  (eV·Å⁶)
  // Note: C_ClCl is 105 here (CsCl-context fit); the NaCl entry uses
  // 116 (NaCl-context). Tosi-Fumi tables are fit per salt — each pair
  // is internally consistent with the rest of that salt's entry.
  'Cl,Cs': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Cs: 1, Cl: -1 },
    pairs: {
      'Cs-Cs': { A: 7598, rho: 0.335, C: 152.0 },
      'Cs-Cl': { A: 4061, rho: 0.335, C: 129.0 },
      'Cl-Cl': { A: 2036, rho: 0.335, C: 105.0 },
    },
  },

  // CsI  — ρ = 0.3370 Å.   CsCl (B2) structure.
  //   exp((σ_i+σ_j)/ρ): CsCs 27124     CsI  47210      II   82210
  //   A = b·B·exp(...): 7152            9956           13006   (eV)
  //   C from Tosi-Fumi:   152.0           278.0          392.0 (eV·Å⁶)
  'Cs,I': {
    source: 'Tosi & Fumi (1964), BMH → exp-6 by σ-fold; D/r⁸ dropped',
    charges: { Cs: 1, I: -1 },
    pairs: {
      'Cs-Cs': { A: 7152, rho: 0.337, C: 152.0 },
      'Cs-I': { A: 9956, rho: 0.337, C: 278.0 },
      'I-I': { A: 13006, rho: 0.337, C: 392.0 },
    },
  },

  // Rocksalt oxides — Lewis & Catlow, J. Phys. C 18 (1985) 1149 (and
  // companion oxide papers). All share the canonical Catlow O–O
  // Buckingham (A=22764.0, ρ=0.1490, C=27.88, identical to the MgO
  // entry's O–O term) and formal ±2 charges; only the cation–O pair
  // differs per salt. Cation–cation pairs are omitted by design —
  // Coulomb alone holds them apart, like Mg-Mg in the MgO entry.
  //
  // The (Mg,Ca,Sr) trend is physically right: heavier cations have
  // softer (larger ρ) Born repulsion at a slightly longer equilibrium.
  // Transition-metal oxides (Mn, Ni) are approximate in a rigid-ion
  // model — d-electron effects matter — but the Lewis-Catlow fit still
  // gives reasonable lattice constants and cohesion.

  // CaO  — Lewis-Catlow Ca²⁺–O²⁻.
  'Ca,O': {
    source: 'Lewis & Catlow (1985), rigid-ion, formal charges',
    charges: { Ca: 2, O: -2 },
    pairs: {
      'Ca-O': { A: 1090.4, rho: 0.3437, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // SrO  — Lewis-Catlow Sr²⁺–O²⁻.
  'O,Sr': {
    source: 'Lewis & Catlow (1985), rigid-ion, formal charges',
    charges: { Sr: 2, O: -2 },
    pairs: {
      'Sr-O': { A: 959.1, rho: 0.3721, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // MnO  — Lewis-Catlow Mn²⁺–O²⁻.  Transition-metal oxide: rigid-ion is
  // an approximation (d-electrons add a magnetic contribution the model
  // ignores), but gives a reasonable lattice and cohesion.
  'Mn,O': {
    source: 'Lewis & Catlow (1985), rigid-ion, formal charges',
    charges: { Mn: 2, O: -2 },
    pairs: {
      'Mn-O': { A: 1007.4, rho: 0.3262, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // NiO  — Lewis-Catlow Ni²⁺–O²⁻.  Same transition-metal caveat as MnO.
  'Ni,O': {
    source: 'Lewis & Catlow (1985), rigid-ion, formal charges',
    charges: { Ni: 2, O: -2 },
    pairs: {
      'Ni-O': { A: 683.5, rho: 0.3332, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // Fluorites — Catlow fluorite library (Catlow 1977 / Lewis-Catlow 1985
  // companion). Rigid-ion, formal +2/-1 charges. All share the canonical
  // Catlow F⁻–F⁻ Buckingham (A=1127.7, ρ=0.2753, C=15.83) — analogous
  // to the rocksalt-oxide family's shared O–O term, but DIFFERENT from
  // the Tosi-Fumi F–F used by LiF/NaF (those are alkali-halide-context
  // fits; each pack is internally self-consistent). The fluorite lattice
  // is the CaF₂ structure (Fm-3m): cations on FCC sites, anions filling
  // the tetrahedral holes — F-F next-nearest-neighbor at a/2.

  // CaF₂  — Catlow Ca²⁺–F⁻.
  'Ca,F': {
    source: 'Catlow fluorite library (Catlow 1977), rigid-ion, formal charges',
    charges: { Ca: 2, F: -1 },
    pairs: {
      'Ca-F': { A: 1272.8, rho: 0.2997, C: 0.0 },
      'F-F': { A: 1127.7, rho: 0.2753, C: 15.83 },
    },
  },

  // SrF₂  — Catlow Sr²⁺–F⁻.
  'F,Sr': {
    source: 'Catlow fluorite library (Catlow 1977), rigid-ion, formal charges',
    charges: { Sr: 2, F: -1 },
    pairs: {
      'Sr-F': { A: 1217.8, rho: 0.317, C: 0.0 },
      'F-F': { A: 1127.7, rho: 0.2753, C: 15.83 },
    },
  },

  // BaF₂  — Catlow Ba²⁺–F⁻.
  'Ba,F': {
    source: 'Catlow fluorite library (Catlow 1977), rigid-ion, formal charges',
    charges: { Ba: 2, F: -1 },
    pairs: {
      'Ba-F': { A: 1380.0, rho: 0.322, C: 0.0 },
      'F-F': { A: 1127.7, rho: 0.2753, C: 15.83 },
    },
  },

  // Perovskites — Bush, Gale, Catlow & Battle (1994) + Lewis-Catlow
  // (1985) titanate library. Rigid-ion, formal +2/+4/-2 charges. Each
  // entry combines:
  //   • Ti⁴⁺–O²⁻        (Bush et al. 1994: A=754.2, ρ=0.3879, C=0)
  //   • A²⁺–O²⁻         (from the Lewis-Catlow oxide series; the same
  //                      Ca/Sr/Ba–O parameters in their rocksalt entries
  //                      above), or Pb²⁺–O² for PbTiO₃
  //   • the canonical Catlow O–O term
  // Cation-cation pairs are omitted (Coulomb only). The ferroelectric
  // tetragonal distortion of BaTiO₃ / PbTiO₃ at low T is reproducible
  // with these parameters; the rigid-ion model captures the bulk of the
  // physics but understates the soft-mode amplitude (shell models do
  // better but are far heavier).

  // SrTiO₃ — Bush + Lewis-Catlow.  Paraelectric reference (the textbook
  // cubic perovskite); near-quantum-paraelectric at low T.
  'O,Sr,Ti': {
    source: 'Bush et al. (1994) Ti–O + Lewis-Catlow (1985) Sr–O / O–O',
    charges: { Sr: 2, Ti: 4, O: -2 },
    pairs: {
      'Sr-O': { A: 959.1, rho: 0.3721, C: 0.0 },
      'O-Ti': { A: 754.2, rho: 0.3879, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // BaTiO₃ — Bush + Lewis-Catlow.  Ferroelectric below ~393 K (the
  // textbook example); the Ti⁴⁺ off-centering that breaks Pm-3m → P4mm
  // is what these parameters were fit to capture.
  'Ba,O,Ti': {
    source: 'Bush et al. (1994) Ti–O + Lewis-Catlow (1985) Ba–O / O–O',
    charges: { Ba: 2, Ti: 4, O: -2 },
    pairs: {
      'Ba-O': { A: 905.7, rho: 0.3976, C: 0.0 },
      'O-Ti': { A: 754.2, rho: 0.3879, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // CaTiO₃ — Bush + Lewis-Catlow.  The mineral that gives perovskites
  // their family name; orthorhombic at room T, cubic above ~1500 K.
  'Ca,O,Ti': {
    source: 'Bush et al. (1994) Ti–O + Lewis-Catlow (1985) Ca–O / O–O',
    charges: { Ca: 2, Ti: 4, O: -2 },
    pairs: {
      'Ca-O': { A: 1090.4, rho: 0.3437, C: 0.0 },
      'O-Ti': { A: 754.2, rho: 0.3879, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // PbTiO₃ — Bush et al. (1995) Pb–O + Bush Ti–O.  Strongly tetragonal
  // ferroelectric at room T (T_c ≈ 763 K). Pb²⁺ has a stereochemically
  // active 6s² lone pair the rigid-ion model can only approximate;
  // shell-model fits do better for the soft-mode quantitatives, but the
  // structural distortion comes out qualitatively right.
  'O,Pb,Ti': {
    source: 'Bush et al. (1994, 1995), rigid-ion, formal charges',
    charges: { Pb: 2, Ti: 4, O: -2 },
    pairs: {
      'O-Pb': { A: 2107.2, rho: 0.3274, C: 19.21 },
      'O-Ti': { A: 754.2, rho: 0.3879, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // Rutile-structure oxides + fluorides — tetragonal P4₂/mnm. Each
  // entry reuses the same cation–anion + anion–anion parameters as the
  // corresponding perovskite or fluorite family (the parameters capture
  // pair chemistry, not the structure: rutile, perovskite and anatase
  // TiO₂ are all valid arrangements of the same Ti⁴⁺/O²⁻ Buckingham).

  // TiO₂ — Bush Ti–O + canonical Catlow O–O.  Used by BOTH the rutile
  // and the anatase preset (same composition → same pack); the lattice
  // parameters of each preset select which polymorph the engine
  // simulates. Rutile is the thermodynamically stable phase above room
  // T; anatase is metastable but the photocatalytically active one.
  'O,Ti': {
    source: 'Bush et al. (1994) Ti–O + Catlow O–O, rigid-ion, formal charges',
    charges: { Ti: 4, O: -2 },
    pairs: {
      'O-Ti': { A: 754.2, rho: 0.3879, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // SnO₂ — Bush et al. (1994) Sn–O + canonical Catlow O–O.  Cassiterite:
  // mineral form of tin, the transparent conductor in indium-tin-oxide.
  'O,Sn': {
    source: 'Bush et al. (1994) Sn–O + Catlow O–O, rigid-ion, formal charges',
    charges: { Sn: 4, O: -2 },
    pairs: {
      'O-Sn': { A: 938.7, rho: 0.3813, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // MgF₂ — Sangster & Atwood (1978) Mg–F + canonical Catlow fluorite
  // F–F. Sellaite: the smallest divalent fluoride; the only +2 fluoride
  // that prefers the rutile over the fluorite structure (Mg²⁺ is too
  // small to support 8-coordinate F⁻).
  'F,Mg': {
    source: 'Sangster & Atwood (1978) Mg–F + canonical fluorite F–F, rigid-ion, formal charges',
    charges: { Mg: 2, F: -1 },
    pairs: {
      'F-Mg': { A: 1828.7, rho: 0.258, C: 0.0 },
      'F-F': { A: 1127.7, rho: 0.2753, C: 15.83 },
    },
  },

  // Li₂O — Catlow Li–O + canonical O–O. Antifluorite structure (Fm-3m):
  // O²⁻ on FCC sites, Li⁺ in BOTH tetrahedral holes — the inverse of
  // CaF₂'s anion/cation occupancy. Lithium battery cathode material in
  // its oxide-shuttling form.
  'Li,O': {
    source: 'Catlow Li–O (Lewis-Catlow 1985 companion) + canonical O–O, rigid-ion, formal charges',
    charges: { Li: 1, O: -2 },
    pairs: {
      'Li-O': { A: 632.1, rho: 0.2906, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // BeO — Lewis-Catlow Be–O + canonical O–O.  Wurtzite (P6₃mc), unlike
  // the rocksalt of the other Group-2 oxides — Be²⁺ is too small to
  // support 6-coordinate O²⁻; it goes 4-coordinate instead. The hardest
  // ionic oxide; ρ_Be-O is small accordingly.
  'Be,O': {
    source: 'Lewis-Catlow Be–O + canonical O–O, rigid-ion, formal charges',
    charges: { Be: 2, O: -2 },
    pairs: {
      'Be-O': { A: 1370.0, rho: 0.263, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // ZnO — Lewis-Catlow/Binks Zn–O + canonical O–O. Wurtzite (P6₃mc),
  // piezoelectric, transparent semiconductor. ZnO has ~30% covalent
  // character — the rigid-ion Buckingham gets lattice and bulk
  // mechanics qualitatively right but understates the piezoelectric
  // response (shell models do better).
  'O,Zn': {
    source: 'Lewis-Catlow/Binks (1994) Zn–O + canonical O–O, rigid-ion, formal charges',
    charges: { Zn: 2, O: -2 },
    pairs: {
      'O-Zn': { A: 700.3, rho: 0.3372, C: 0.0 },
      'O-O': { A: 22764.0, rho: 0.149, C: 27.88 },
    },
  },

  // PbS — Bush et al. galena-specific Pb–S + Catlow-style S–S. Rocksalt
  // structure (galena). Pb²⁺ has the same 6s² lone-pair caveat as in
  // PbTiO₃; rigid-ion + formal charges captures the dominant
  // electrostatics but not the lone-pair-driven phonon softening. The
  // S–S Buckingham is shared with CdS below.
  'Pb,S': {
    source: 'Bush et al. galena Pb–S + Catlow-family S–S, rigid-ion, formal charges',
    charges: { Pb: 2, S: -2 },
    pairs: {
      'Pb-S': { A: 2090.5, rho: 0.336, C: 0.0 },
      'S-S': { A: 1200.0, rho: 0.149, C: 120.0 },
    },
  },

  // CdS — Wright et al. (1995) Cd–S + Catlow-style S–S. Wurtzite
  // (greenockite). CdS is the most covalent of the entries here
  // (~40% covalent character); rigid-ion is genuinely borderline,
  // but gives reasonable lattice and qualitative dynamics. Reuses
  // the S–S term introduced for PbS.
  'Cd,S': {
    source: 'Wright et al. (1995) Cd–S + Catlow-family S–S, rigid-ion, formal charges',
    charges: { Cd: 2, S: -2 },
    pairs: {
      'Cd-S': { A: 1014.8, rho: 0.3878, C: 0.0 },
      'S-S': { A: 1200.0, rho: 0.149, C: 120.0 },
    },
  },

  // SiO₂ (cristobalite, quartz, ...) — BKS (van Beest, Kramer, van
  // Santen 1990, PRL 64, 1955). The default silica potential in the
  // literature: Buckingham short-range + partial-charge Coulomb,
  // designed for the corner-sharing SiO₄ tetrahedral network. Despite
  // SiO₂ being a covalent network solid in reality, BKS uses
  // PARTIAL ionic charges (q_Si = +2.4, q_O = -1.2 — NOT formal ±4 /
  // ±2) and treats the Si-O bond as a Buckingham + Coulomb pair.
  // This unusual choice is what makes BKS the standard: it captures
  // the silica network's mechanics, lattice constant, elastic
  // constants and phase transitions far better than a formal-charge
  // model would. Reproduces α-quartz, β-cristobalite, β-tridymite and
  // all the dense polymorphs with one parameter set.
  //
  // Known caveat — Buckingham short-range: BKS does NOT add an inner
  // r⁻¹² wall. At unphysically short Si-O separations (< ~1.2 Å) the
  // Buckingham turns attractive and a structure could collapse. The
  // experimental Si-O bond at 1.55 Å is comfortably outside this
  // regime; static cohesion and standard-temperature MD are fine, but
  // a high-T melt simulation would want the Carré et al. (2008)
  // tabulated short-range fix.
  //
  // Si-Si has NO short-range Buckingham term — the +2.4 / +2.4 Coulomb
  // repulsion alone keeps Si atoms apart in the network.
  'O,Si': {
    source: 'BKS (van Beest, Kramer, van Santen 1990), partial charges',
    charges: { Si: 2.4, O: -1.2 },
    pairs: {
      'O-Si': { A: 18003.7572, rho: 0.2052, C: 133.5381 },
      'O-O': { A: 1388.773, rho: 0.36232, C: 175.0 },
    },
    // BKS has no inner r⁻¹² wall and partial charges (not formal),
    // so the relaxer collapses Si-O from the loaded 1.55 Å to ~1.0 Å
    // (the unphysical inner minimum). The preset geometry IS the
    // experimental equilibrium — leave it where it is.
    skipMinimize: true,
    // TraPPE-zeo (Bai, Tsapatsis & Siepmann, J. Phys. Chem. C 117, 24375,
    // 2013) — the framework LJ fitted to adsorption in all-silica zeolites.
    guestHostLJ: {
      O: { sigma: 3.3, epsilonK: 53.0, charge: -0.7574 },
      Si: { sigma: 2.3, epsilonK: 22.0, charge: 1.5148 },
    },
  },

  // Pyrite FeS₂ — Sithole-style partial charges + tailored S-S
  // Buckingham approximating the persulfide covalent dimer. Pyrite is
  // GENUINELY HYBRID: Fe²⁺ octahedrally coordinated by 6 S (ionic-ish,
  // Fe-S = 2.27 Å) AND short S-S persulfide dimers at 2.18 Å (covalent).
  // Published high-fidelity FFs (Sithole 1999, Wright & Gale 2003)
  // capture the dimer with a MORSE pair — which our pure-Buckingham
  // infrastructure doesn't have — so this pack approximates the Morse
  // by a tailored deep Buckingham well centered at the dimer distance.
  //
  // Parameters and rationale:
  //   q_Fe = +1.0, q_S = -0.5 (Sithole-style; the real bonding has
  //           significant covalent character so formal ±2/-1 over-states
  //           the Madelung sum — partial charges land cohesion in the
  //           realistic -15 to -20 eV/FeS₂ range)
  //   Fe-S: A = 14644, ρ = 0.227, C = 14.5 (Sithole 1999 directly)
  //   S-S: A = 18372, ρ = 0.30, C = 1693 — tailored so combined Coulomb
  //          (+1.65 eV/pair at 2.18 with q_S = -0.5) plus Buckingham
  //          (-3 eV/pair well at 2.18) gives total ≈ -1.4 eV/pair at
  //          the persulfide bond length, with the gradient balanced so
  //          the dimer is in mechanical equilibrium at 2.18 Å.
  //
  // skipMinimize is essential: the S-S well is intentionally shallow
  // (~3 eV) and the relaxer can easily push the dimer through the well
  // into the unphysical inner Buckingham minimum. The preset geometry
  // already sits at the experimental dimer length.
  //
  // Cohesion at the preset geometry: ~-17.5 eV/FeS₂, within the
  // literature lattice-energy range. MD with this pack: usable for
  // short runs at low T; long-T MD will eventually distort the dimers
  // because the Buckingham approximation to the Morse bond is softer
  // than reality. Replace with a hybrid Buckingham + Morse pack if
  // PairPotential ever grows Morse support for ionic packs.
  'Fe,S': {
    source: 'Sithole-style partial charges + tailored persulfide S-S Buckingham',
    charges: { Fe: 1, S: -0.5 },
    pairs: {
      'Fe-S': { A: 14644, rho: 0.227, C: 14.5 },
      'S-S': { A: 18372, rho: 0.3, C: 1693 },
    },
    skipMinimize: true,
  },
};

/** Composition key for a crystal: sorted unique basis element symbols. */
const compositionKey = (crystal: CrystalState): string =>
  [...new Set(crystal.basis.map((b) => b.element))].sort().join(',');

/** The ionic Buckingham pack for `crystal`, or null if none is registered. */
export const ionicPackFor = (crystal: CrystalState): IonicPack | null => IONIC_PACKS[compositionKey(crystal)] ?? null;
